/**
 * App-level notification preferences (DOP-274 round 2).
 *
 * Browser permission is only half the answer. A user can have granted Chrome
 * the right to show banners and still not want one for every extraction, and —
 * as live testing proved — a granted permission is no guarantee anything is
 * visible at all, because macOS gates Chrome itself. So the app keeps its own
 * preference next to the browser's, and the Settings page teaches the rest.
 *
 * All of it lives in `localStorage`: this is a per-device UI preference, not
 * account state, and a device is exactly the scope at which "notify me here"
 * means something. Every read is wrapped — Safari private mode throws on
 * access — and an unreadable store simply means "defaults".
 */

const DESKTOP_ENABLED_KEY = "doppler.notifications.desktopEnabled";
const SOUND_ENABLED_KEY = "doppler.notifications.soundEnabled";
const SOFT_ASK_DISMISSALS_KEY = "doppler.notifications.softAskDismissals";

/**
 * How many times the soft-ask may be ignored before it stops appearing.
 *
 * Two, not one: the first dismissal is as likely to be "not now" as "never",
 * and unlike the browser prompt this ask costs nothing to show again once.
 */
export const SOFT_ASK_DISMISSAL_LIMIT = 2;

const readRaw = (key: string): string | null => {
	try {
		return window.localStorage.getItem(key);
	} catch {
		return null;
	}
};

const writeRaw = (key: string, value: string): void => {
	try {
		window.localStorage.setItem(key, value);
	} catch {
		// A full or blocked store costs the user a remembered preference, which
		// is not worth throwing on a notification path.
	}
};

/** Booleans default to ON: the stored value only ever records an opt-OUT. */
const readFlag = (key: string): boolean => readRaw(key) !== "false";

/**
 * Does the user want desktop notifications from this app on this device?
 *
 * Independent of the browser permission — this is the switch the Settings page
 * shows once permission has been granted.
 */
export const isDesktopNotificationsEnabled = (): boolean =>
	readFlag(DESKTOP_ENABLED_KEY);

export const setDesktopNotificationsEnabled = (enabled: boolean): void =>
	writeRaw(DESKTOP_ENABLED_KEY, enabled ? "true" : "false");

/**
 * The in-browser chime for a finished extraction.
 *
 * Independent of the desktop preference and of the browser permission: Web
 * Audio asks nobody, so a user whose OS blocks banners can still be told.
 */
export const isNotificationSoundEnabled = (): boolean =>
	readFlag(SOUND_ENABLED_KEY);

export const setNotificationSoundEnabled = (enabled: boolean): void =>
	writeRaw(SOUND_ENABLED_KEY, enabled ? "true" : "false");

const readDismissals = (): number => {
	const parsed = Number.parseInt(readRaw(SOFT_ASK_DISMISSALS_KEY) ?? "", 10);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

/** The user ignored or dismissed the soft-ask. Counted, never prompted. */
export const recordSoftAskDismissed = (): void =>
	writeRaw(SOFT_ASK_DISMISSALS_KEY, String(readDismissals() + 1));

/** Stop offering: they have said "not now" as often as we are going to ask. */
export const hasOptedOutOfSoftAsk = (): boolean =>
	readDismissals() >= SOFT_ASK_DISMISSAL_LIMIT;
